import { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search, Plus, Package, Pencil, Trash2, CheckCircle2, XCircle, AlertTriangle, UploadCloud, X, RotateCcw, Film, Image as ImageIcon } from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select, { Pagination } from '../../common/Select/Select'
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  clearProductError,
} from './services/productSlice'
import { fetchCategories } from '../Category/services/categorySlice'

/* Hero banner nu fix size - backend (ProductController::BANNER_WIDTH/HEIGHT) ma pan same rakhvu */
const BANNER_W = 2000
const BANNER_H = 755

const GST_OPTIONS = [
  { value: 0, label: '0% (Exempt)' },
  { value: 5, label: '5%' },
  { value: 12, label: '12%' },
  { value: 18, label: '18%' },
  { value: 28, label: '28%' },
]

const TAX_MODE_OPTIONS = [
  { value: 1, label: 'Price includes GST (MRP inclusive)' },
  { value: 0, label: 'GST extra (checkout par add thase)' },
]

const money2 = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const STATUS_OPTIONS = [
  { value: 1, label: 'active' },
  { value: 0, label: 'inactive' },
]

const makeSlug = (text) =>
  text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

// textarea (ek line = ek item) <-> array
const toLines = (text) => text.split('\n').map((l) => l.trim()).filter(Boolean)

const EMPTY_FORM = {
  category_id: '',
  name: '',
  slug: '',
  sku: '',
  short_description: '',
  description: '',
  features: '',                 // textarea, ek line = ek feature
  personalization_notes: 'No special characters can be embossed\nUpper case alphabets and numbers only\nText personalization limit: 20 characters\nLogo/image upload: JPG, PNG, SVG (Max. 5MB)',
  shipping_info: 'We provide free shipping in India on orders above Rs.999\nStandard shipping timelines: Delivery within 1-3 working days\nSame day shipping available only in Mumbai',
  return_policy: 'Personalized/customised products are not eligible for return or cancellation.\nReturns are accepted only for damaged, defective, or incorrect products within 7 days of delivery.',
  price: '',
  old_price: '',
  hsn_code: '',
  gst_rate: 5,
  price_includes_tax: true,
  stock_qty: 0,
  sort_order: 0,
  rating_avg: '',
  reviews_count: 0,
  status: 1,
  is_new: false,
  is_bestseller: false,
  is_featured: false,
  is_hero: false,
  is_customizable: false,
  allow_text_print: true,
  allow_logo_upload: true,
  text_max_length: 20,
  customization_price: 0,
  gift_wrap_price: 99,
  likes: '',
  gift_for: '',
  occasion: '',
  meta_title: '',
  meta_description: '',
  colors: [{ name: '', hex: '#D8B87A' }],
  related_ids: [],
  bought_together_ids: [],
  // media
  new_images: [],               // File[]
  remove_image_ids: [],         // existing image ids je kadhvi chhe
  video: null,
  video_poster: null,
  remove_video: false,
  // hero banner
  banner_image: null,           // File (2000 x 755)
  banner_mobile_image: null,    // File (optional)
  remove_banner: false,
  remove_banner_mobile: false,
}

const SCALAR_KEYS = [
  'category_id', 'name', 'slug', 'sku', 'short_description', 'description',
  'shipping_info', 'return_policy', 'price', 'old_price', 'hsn_code', 'gst_rate', 'price_includes_tax',
  'stock_qty', 'sort_order', 'rating_avg', 'reviews_count', 'status',
  'is_new', 'is_bestseller', 'is_featured', 'is_hero', 'is_customizable', 'allow_text_print', 'allow_logo_upload',
  'text_max_length', 'customization_price', 'gift_wrap_price',
  'likes', 'gift_for', 'occasion', 'meta_title', 'meta_description',
]

const formatPrice = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

/* ── Product picker: search + click to add, chips to remove ── */
function ProductPicker({ options, selectedIds, onChange, placeholder = 'Search product…' }) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const selected = selectedIds
    .map((id) => options.find((p) => p.id === id))
    .filter(Boolean)

  const suggestions = options
    .filter((p) => !selectedIds.includes(p.id))
    .filter((p) => {
      const q = query.trim().toLowerCase()
      return !q || p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q)
    })
    .slice(0, 30)

  const add = (id) => { onChange([...selectedIds, id]); setQuery('') }
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id))

  return (
    <div className="pp-wrap" ref={wrapRef}>
      {selected.length > 0 && (
        <div className="pp-chips">
          {selected.map((p) => (
            <span key={p.id} className="pp-chip">
              {p.img && <img src={p.img} alt="" />}
              <span className="pp-chip-name">{p.name}</span>
              <button type="button" onClick={() => remove(p.id)} aria-label={`Remove ${p.name}`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        className="input"
        placeholder={placeholder}
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
      />

      {open && (
        <div className="pp-menu">
          {options.length === 0 && <div className="pp-empty">No other products yet</div>}
          {options.length > 0 && suggestions.length === 0 && <div className="pp-empty">No matching product</div>}
          {suggestions.map((p) => (
            <button type="button" key={p.id} className="pp-item" onClick={() => add(p.id)}>
              {p.img ? <img src={p.img} alt="" /> : <span className="pp-noimg"><Package size={12} /></span>}
              <span className="pp-item-name">{p.name}</span>
              <span className="pp-item-price">{formatPrice(p.price)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Gallery upload: multiple photos + 1 video ── */
function GalleryUpload({ existing, removedIds, newFiles, onAddFiles, onToggleRemove, onRemoveNew }) {
  const inputRef = useRef(null)
  const previews = useMemo(() => newFiles.map((f) => URL.createObjectURL(f)), [newFiles])

  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews])

  const pick = (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith('image/'))
    if (files.length) onAddFiles(files)
  }

  return (
    <div>
      <input
        ref={inputRef} type="file" accept="image/*" multiple hidden
        onChange={(e) => { pick(e.target.files); e.target.value = '' }}
      />
      <div className="prd-gallery">
        {existing.map((img, i) => {
          const removed = removedIds.includes(img.id)
          return (
            <div key={img.id} className={`prd-thumb ${removed ? 'removed' : ''}`}>
              <img src={img.url} alt={`Product ${i + 1}`} />
              {i === 0 && !removed && <span className="prd-main-tag">Main</span>}
              <button type="button" className="prd-thumb-btn" onClick={() => onToggleRemove(img.id)}
                aria-label={removed ? 'Undo remove' : 'Remove image'}>
                {removed ? <RotateCcw size={12} /> : <X size={12} />}
              </button>
            </div>
          )
        })}
        {previews.map((src, i) => (
          <div key={src} className="prd-thumb">
            <img src={src} alt={`New ${i + 1}`} />
            <span className="prd-new-tag">New</span>
            <button type="button" className="prd-thumb-btn" onClick={() => onRemoveNew(i)} aria-label="Remove">
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" className="prd-add" onClick={() => inputRef.current?.click()}>
          <UploadCloud size={18} />
          <span>Add photos</span>
        </button>
      </div>
    </div>
  )
}

/* ── Banner upload: ek image, optional exact-size check ──
   exactSize = [width, height]  -> bhul size hoy to reject
   exactSize = null             -> koi size check nahi (mobile banner)           */
function BannerUpload({
  existingUrl, removed, file, exactSize, ratio,
  onPick, onRemoveNew, onToggleRemove, onError, error,
}) {
  const inputRef = useRef(null)
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

  const pick = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return

    if (!f.type.startsWith('image/')) { onError('Please select an image file only'); return }
    if (f.size > 5 * 1024 * 1024) { onError('Image must be smaller than 5MB'); return }

    if (!exactSize) { onError(''); onPick(f); return }

    const url = URL.createObjectURL(f)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      if (img.naturalWidth !== exactSize[0] || img.naturalHeight !== exactSize[1]) {
        onError(`Size must be exactly ${exactSize[0]} × ${exactSize[1]} px. Your image: ${img.naturalWidth} × ${img.naturalHeight} px`)
      } else {
        onError('')
        onPick(f)
      }
    }
    img.onerror = () => { URL.revokeObjectURL(url); onError('Could not read the image') }
    img.src = url
  }

  const shown = preview || (existingUrl && !removed ? existingUrl : null)

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} />

      <div className="prd-banner-box" style={ratio ? { aspectRatio: ratio } : undefined}>
        {shown ? (
          <img src={shown} alt="Banner preview" />
        ) : (
          <div className="prd-banner-empty">
            <ImageIcon size={22} />
            <span>{removed ? 'Banner removed' : 'No banner uploaded'}</span>
          </div>
        )}
        {preview && <span className="prd-new-tag">New</span>}
      </div>

      <div className="prd-file-row" style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => inputRef.current?.click()}>
          <UploadCloud size={13} /> {shown ? 'Replace banner' : 'Upload banner'}
        </button>
        {file && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRemoveNew}>Cancel new</button>
        )}
        {!file && existingUrl && !removed && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onToggleRemove}>Remove</button>
        )}
        {!file && existingUrl && removed && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onToggleRemove}>Undo remove</button>
        )}
      </div>
      {error && <small style={{ color: '#ef4444', display: 'block', marginTop: 4 }}>{error}</small>}
    </div>
  )
}

export default function Product() {
  const dispatch = useDispatch()
  const { list: products, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.product)
  const categories = useSelector((state) => state.category.list)

  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const [bannerError, setBannerError] = useState('')
  const [bannerMobileError, setBannerMobileError] = useState('')

  const videoRef = useRef(null)
  const posterRef = useRef(null)

  useEffect(() => {
    dispatch(fetchProducts())
    dispatch(fetchCategories())
  }, [dispatch])

  /* ── Select options ── */
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.parent_name ? `${c.parent_name} › ${c.name}` : c.name })),
    [categories]
  )
  const categoryFilterOptions = useMemo(
    () => [{ value: '', label: 'All Categories' }, ...categoryOptions],
    [categoryOptions]
  )
  const formCategoryOptions = useMemo(
    () => [{ value: '', label: '— No category —' }, ...categoryOptions],
    [categoryOptions]
  )
  const statusFilterOptions = [
    { value: '', label: 'All Status' },
    { value: 1, label: 'active' },
    { value: 0, label: 'inactive' },
  ]

  // related / bought together ma pote ne na batavo
  const otherProducts = useMemo(
    () => products.filter((p) => !active || p.id !== active.id),
    [products, active]
  )

  /* ── Filter + pagination ── */
  const filtered = products.filter((p) => {
    const q = search.toLowerCase()
    const matchesSearch =
      p.name.toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q)
    if (!matchesSearch) return false
    if (categoryFilter !== '' && String(p.category_id) !== String(categoryFilter)) return false
    if (statusFilter !== '' && String(p.status) !== String(statusFilter)) return false
    return true
  })

  useEffect(() => { setPage(1) }, [search, categoryFilter, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handlePageSizeChange = (size) => { setPageSize(size); setPage(1) }

  /* ── form helpers ── */
  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))

  // Amazon jevu live price breakdown
  const priceInfo = useMemo(() => {
    const price = Number(form.price) || 0
    const mrp = Number(form.old_price) || 0
    const rate = Number(form.gst_rate) || 0
    const off = mrp > price ? mrp - price : 0
    const pct = mrp > price ? Math.round((off / mrp) * 100) : 0
    const taxable = form.price_includes_tax ? price / (1 + rate / 100) : price
    const gst = form.price_includes_tax ? price - taxable : (price * rate) / 100
    return { price, mrp, off, pct, taxable, gst, rate, final: form.price_includes_tax ? price : price + gst }
  }, [form.price, form.old_price, form.gst_rate, form.price_includes_tax])

  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const setColor = (i, key, value) =>
    setForm((f) => ({ ...f, colors: f.colors.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)) }))
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { name: '', hex: '#232323' }] }))
  const removeColor = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }))

  // native <select multiple> -> number[]
  const pickMulti = (name, e) =>
    setField(name, Array.from(e.target.selectedOptions).map((o) => Number(o.value)))

  const resetBannerErrors = () => { setBannerError(''); setBannerMobileError('') }

  const openAdd = () => {
    dispatch(clearProductError())
    resetBannerErrors()
    setActive(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (p) => {
    dispatch(clearProductError())
    resetBannerErrors()
    setActive(p)
    setForm({
      ...EMPTY_FORM,
      category_id: p.category_id || '',
      name: p.name,
      slug: p.slug,
      sku: p.sku || '',
      short_description: p.short_description || '',
      description: p.description || '',
      features: (p.features || []).join('\n'),
      personalization_notes: (p.personalization_notes || []).join('\n'),
      shipping_info: p.shipping_info || '',
      return_policy: p.return_policy || '',
      price: p.price,
      old_price: p.old_price ?? '',
      hsn_code: p.hsn_code || '',
      gst_rate: p.gst_rate ?? 5,
      price_includes_tax: p.price_includes_tax !== false,
      stock_qty: p.stock_qty,
      sort_order: p.sort_order,
      rating_avg: p.rating || '',
      reviews_count: p.reviews || 0,
      status: p.status,
      is_new: p.is_new,
      is_bestseller: p.is_bestseller,
      is_featured: p.is_featured,
      is_hero: !!p.is_hero,
      is_customizable: p.is_customizable,
      allow_text_print: p.allow_text_print,
      allow_logo_upload: p.allow_logo_upload,
      text_max_length: p.text_max_length,
      customization_price: p.customization_price,
      gift_wrap_price: p.gift_wrap_price,
      likes: p.likes || '',
      gift_for: p.gift_for || '',
      occasion: p.occasion || '',
      meta_title: p.meta_title || '',
      meta_description: p.meta_description || '',
      colors: p.colors?.length ? p.colors : [{ name: '', hex: '#D8B87A' }],
      related_ids: p.related_ids || [],
      bought_together_ids: p.bought_together_ids || [],
    })
    setModalOpen(true)
  }

  const buildFormData = () => {
    const fd = new FormData()

    if (active) fd.append('id', active.id)

    SCALAR_KEYS.forEach((key) => {
      const val = form[key]
      if (typeof val === 'boolean') fd.append(key, val ? 1 : 0)
      else if (val !== null && val !== undefined) fd.append(key, val)
    })

    // list / json fields
    fd.append('features', JSON.stringify(toLines(form.features)))
    fd.append('personalization_notes', JSON.stringify(toLines(form.personalization_notes)))
    fd.append('colors', JSON.stringify(form.colors.filter((c) => c.name.trim())))
    fd.append('related_ids', JSON.stringify(form.related_ids))
    fd.append('bought_together_ids', JSON.stringify(form.bought_together_ids))

    // media
    form.new_images.forEach((f) => fd.append('images[]', f))
    fd.append('remove_image_ids', JSON.stringify(form.remove_image_ids))
    if (form.video instanceof File) fd.append('video', form.video)
    if (form.video_poster instanceof File) fd.append('video_poster', form.video_poster)
    fd.append('remove_video', form.remove_video ? 1 : 0)

    // hero banner
    if (form.banner_image instanceof File) fd.append('banner_image', form.banner_image)
    if (form.banner_mobile_image instanceof File) fd.append('banner_mobile_image', form.banner_mobile_image)
    fd.append('remove_banner', form.remove_banner ? 1 : 0)
    fd.append('remove_banner_mobile', form.remove_banner_mobile ? 1 : 0)

    return fd
  }

  const save = async () => {
    if (!form.name.trim() || form.price === '') return

    // Hero ON hoy to banner hovu j joiye (nayu upload ya existing)
    const hasBanner = form.banner_image instanceof File || (active?.banner && !form.remove_banner)
    if (form.is_hero && !hasBanner) {
      setBannerError('Upload a banner to show in the Hero slider')
      return
    }

    const fd = buildFormData()

    const result = active
      ? await dispatch(updateProduct(fd))
      : await dispatch(createProduct(fd))

    if (result.meta.requestStatus === 'fulfilled') {
      setModalOpen(false)
    }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteProduct(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
    }
  }

  /* ── stats ── */
  const total = products.length
  const activeCount = products.filter((p) => p.status === 1).length
  const inactiveCount = products.filter((p) => p.status === 0).length
  const outOfStock = products.filter((p) => !p.in_stock).length

  const stats = [
    { label: 'Total Products', value: total, icon: Package, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', variant: 'indigo' },
    { label: 'Active', value: activeCount, icon: CheckCircle2, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', variant: 'green' },
    { label: 'Inactive', value: inactiveCount, icon: XCircle, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', variant: 'red' },
    { label: 'Out of Stock', value: outOfStock, icon: AlertTriangle, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', variant: 'amber' },
  ]

  const existingImages = active?.images || []
  const existingVideo = active?.video && !form.remove_video ? active.video : null

  return (
    <div className="dash-page page-in">
      {/* ── PAGE HEADER ── */}
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Products</h1>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add Product</button>
      </div>

      {/* ── STATS ── */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position: 'absolute', top: 0, right: 0, width: 110, height: 110,
              borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
              transform: 'translate(30%, -30%)', pointerEvents: 'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.value}
              </span>
            </div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      {/* ── TABLE ── */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label className="tb-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search name, slug or SKU…"
              aria-label="Search products"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <Select
            className="cat-filter-select"
            ariaLabel="Category filter"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryFilterOptions}
            searchable={categoryFilterOptions.length > 8}
            minMenuWidth={220}
          />
          <Select
            className="cat-filter-select"
            ariaLabel="Status filter"
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusFilterOptions}
          />

          {(categoryFilter !== '' || statusFilter !== '' || search) && (
            <button
              type="button" className="btn btn-outline btn-sm"
              onClick={() => { setSearch(''); setCategoryFilter(''); setStatusFilter('') }}
            >
              Clear filters
            </button>
          )}
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Product ID</th><th>Product</th><th>Category</th>
                <th>Price</th><th>Stock</th><th>Reviews</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text3)' }}>No products found</td></tr>
              )}
              {paginated.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>
                    PRD-{String(p.id).padStart(3, '0')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                      {p.img ? (
                        <img src={p.img} alt={p.name}
                          style={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 40, height: 40, borderRadius: 'var(--radius-md)', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'var(--bg-raised)', color: 'var(--text3)',
                        }}>
                          <Package size={16} />
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, color: 'var(--text1)' }}>
                          {p.name}
                          {p.is_new && <span className="badge badge-gold" style={{ marginLeft: 6 }}>New</span>}
                          {p.is_bestseller && <span className="badge badge-gold" style={{ marginLeft: 6 }}>Bestseller</span>}
                          {p.is_customizable && <span className="badge badge-gold" style={{ marginLeft: 6 }}>Customizable</span>}
                          {p.is_hero && <span className="badge badge-gold" style={{ marginLeft: 6 }}>Hero</span>}
                        </div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                          {p.sku || p.slug}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{p.category_name || '—'}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{formatPrice(p.price)}</div>
                    {p.old_price ? (
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                        <s>{formatPrice(p.old_price)}</s> {p.off && <span style={{ color: '#10B981' }}>{p.off}</span>}
                      </div>
                    ) : null}
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>
                      GST {Number(p.gst_rate || 0)}% {p.price_includes_tax === false ? '(extra)' : '(incl.)'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${p.in_stock ? 'badge-active' : 'badge-inactive'}`}>
                      {p.in_stock ? `${p.stock_qty} in stock` : 'out of stock'}
                    </span>
                  </td>
                  <td>
                    {p.reviews > 0 ? `★ ${Number(p.rating).toFixed(1)} (${p.reviews})` : '—'}
                  </td>
                  <td>
                    <span className={`badge ${p.status === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {p.status === 1 ? 'active' : 'inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(p)}><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(p); setDeleteOpen(true) }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isLoading && filtered.length > 0 && (
          <Pagination
            total={filtered.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* ── ADD / EDIT MODAL ── */}
      <Modal
        open={modalOpen}
        title={active ? 'Edit Product' : 'Add Product'}
        subtitle="Shown on the product listing and description pages"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={isSaving}>
              {isSaving ? 'Saving…' : active ? 'Update Product' : 'Create Product'}
            </button>
          </>
        )}
      >
        {isError && message && (
          <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>
        )}

        <div className="form-grid cols-2">
          {/* ---- Basic ---- */}
          <div className="form-field">
            <label>Product Name</label>
            <input
              className="input" placeholder="e.g. Floral Print Tote Bag" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value, slug: active ? form.slug : makeSlug(e.target.value) })}
            />
            {fieldError('name')}
          </div>
          <div className="form-field">
            <label>Slug</label>
            <input className="input" placeholder="floral-print-tote-bag" value={form.slug}
              onChange={(e) => setField('slug', makeSlug(e.target.value))} />
            {fieldError('slug')}
          </div>

          <div className="form-field">
            <label>Category</label>
            <Select
              value={form.category_id}
              onChange={(v) => setField('category_id', v)}
              options={formCategoryOptions}
              searchable={formCategoryOptions.length > 8}
              placeholder="— No category —"
            />
            {fieldError('category_id')}
          </div>
          <div className="form-field">
            <label>SKU</label>
            <input className="input" placeholder="SUP-TOTE-001" value={form.sku}
              onChange={(e) => setField('sku', e.target.value)} />
            {fieldError('sku')}
          </div>

          <div className="form-field">
            <label>Price (₹)</label>
            <input type="number" min={0} className="input" value={form.price}
              onChange={(e) => setField('price', e.target.value)} />
            {fieldError('price')}
          </div>
          <div className="form-field">
            <label>Old Price / MRP (₹)</label>
            <input type="number" min={0} className="input" value={form.old_price}
              onChange={(e) => setField('old_price', e.target.value)} />
            {fieldError('old_price')}
          </div>

          {/* ---- GST ---- */}
          <div className="form-field">
            <label>HSN Code</label>
            <input className="input" placeholder="e.g. 4202" maxLength={8} value={form.hsn_code}
              onChange={(e) => setField('hsn_code', e.target.value.replace(/\D/g, ''))} />
            {fieldError('hsn_code')}
          </div>
          <div className="form-field">
            <label>GST Rate</label>
            <Select value={Number(form.gst_rate)} onChange={(v) => setField('gst_rate', Number(v))} options={GST_OPTIONS} />
            {fieldError('gst_rate')}
          </div>
          <div className="form-field full">
            <label>Price type</label>
            <Select
              value={form.price_includes_tax ? 1 : 0}
              onChange={(v) => setField('price_includes_tax', Number(v) === 1)}
              options={TAX_MODE_OPTIONS}
            />
          </div>

          {priceInfo.price > 0 && (
            <div className="form-field full">
              <div className="prd-taxbox">
                <div><span>Selling price</span><b>{money2(priceInfo.price)}</b></div>
                {priceInfo.off > 0 && (
                  <>
                    <div><span>MRP</span><b><s>{money2(priceInfo.mrp)}</s></b></div>
                    <div><span>Discount</span><b style={{ color: '#10B981' }}>−{money2(priceInfo.off)} ({priceInfo.pct}% off)</b></div>
                  </>
                )}
                <div><span>Taxable value</span><b>{money2(priceInfo.taxable)}</b></div>
                <div><span>GST @ {priceInfo.rate}%</span><b>{money2(priceInfo.gst)}</b></div>
                <div className="prd-taxbox-total">
                  <span>Customer pays (per unit)</span><b>{money2(priceInfo.final)}</b>
                </div>
              </div>
            </div>
          )}

          <div className="form-field">
            <label>Stock Qty</label>
            <input type="number" min={0} className="input" value={form.stock_qty}
              onChange={(e) => setField('stock_qty', e.target.value)} />
            {fieldError('stock_qty')}
          </div>
          <div className="form-field">
            <label>Status</label>
            <Select value={form.status} onChange={(v) => setField('status', Number(v))} options={STATUS_OPTIONS} />
          </div>

          {/* ---- Descriptions ---- */}
          <div className="form-field full">
            <label>Short Description</label>
            <textarea className="input" rows={2} placeholder="Shown below the price on the product page"
              value={form.short_description} onChange={(e) => setField('short_description', e.target.value)} />
            {fieldError('short_description')}
          </div>
          <div className="form-field full">
            <label>Description (Description &amp; Features)</label>
            <textarea className="input" rows={3} value={form.description}
              onChange={(e) => setField('description', e.target.value)} />
          </div>
          <div className="form-field full">
            <label>Features <small style={{ color: 'var(--text3)' }}>(one per line)</small></label>
            <textarea className="input" rows={4} placeholder={'Material: Premium cotton canvas\nDimensions: 15" x 16" x 4"'}
              value={form.features} onChange={(e) => setField('features', e.target.value)} />
          </div>

          {/* ---- Media ---- */}
          <div className="form-field full">
            <label>Gallery Photos <small style={{ color: 'var(--text3)' }}>(first photo = main image · PNG, JPG, WEBP · max 5MB each)</small></label>
            <GalleryUpload
              existing={existingImages}
              removedIds={form.remove_image_ids}
              newFiles={form.new_images}
              onAddFiles={(files) => setForm((f) => ({ ...f, new_images: [...f.new_images, ...files] }))}
              onToggleRemove={(id) => setForm((f) => ({
                ...f,
                remove_image_ids: f.remove_image_ids.includes(id)
                  ? f.remove_image_ids.filter((x) => x !== id)
                  : [...f.remove_image_ids, id],
              }))}
              onRemoveNew={(i) => setForm((f) => ({ ...f, new_images: f.new_images.filter((_, idx) => idx !== i) }))}
            />
            {fieldError('images')}
            {errors && Object.keys(errors).filter((k) => k.startsWith('images.')).slice(0, 1).map((k) => (
              <small key={k} style={{ color: '#ef4444' }}>{errors[k][0]}</small>
            ))}
          </div>

          {/* ---- Hero / Advertisement banner ---- */}
          <div className="form-field full prd-hero-block">
            <label style={{ fontWeight: 600 }}>
              Hero / Advertisement Banner{' '}
              <small style={{ color: 'var(--text3)' }}>
                (home page top slider · exactly {BANNER_W} × {BANNER_H} px · JPG, PNG, WEBP · max 5MB)
              </small>
            </label>

            <label className="prd-hero-toggle">
              <input type="checkbox" checked={form.is_hero}
                onChange={(e) => setField('is_hero', e.target.checked)} />
              Show this product's banner in the home page hero slider
            </label>

            <BannerUpload
              existingUrl={active?.banner}
              removed={form.remove_banner}
              file={form.banner_image}
              exactSize={[BANNER_W, BANNER_H]}
              ratio={`${BANNER_W} / ${BANNER_H}`}
              error={bannerError || (errors?.banner_image?.[0] ?? '')}
              onError={setBannerError}
              onPick={(f) => setForm((s) => ({ ...s, banner_image: f, remove_banner: false }))}
              onRemoveNew={() => { setField('banner_image', null); setBannerError('') }}
              onToggleRemove={() => setField('remove_banner', !form.remove_banner)}
            />
          </div>

          <div className="form-field full">
            <label>
              Mobile Banner <small style={{ color: 'var(--text3)' }}>(optional · shown on phones · use the same size as your current mobile banners)</small>
            </label>
            <BannerUpload
              existingUrl={active?.banner_mobile}
              removed={form.remove_banner_mobile}
              file={form.banner_mobile_image}
              exactSize={null}
              ratio="16 / 9"
              error={bannerMobileError || (errors?.banner_mobile_image?.[0] ?? '')}
              onError={setBannerMobileError}
              onPick={(f) => setForm((s) => ({ ...s, banner_mobile_image: f, remove_banner_mobile: false }))}
              onRemoveNew={() => { setField('banner_mobile_image', null); setBannerMobileError('') }}
              onToggleRemove={() => setField('remove_banner_mobile', !form.remove_banner_mobile)}
            />
          </div>

          <div className="form-field">
            <label>Product Video <small style={{ color: 'var(--text3)' }}>(mp4 / webm · max 20MB)</small></label>
            <input ref={videoRef} type="file" accept="video/mp4,video/webm" hidden
              onChange={(e) => { setField('video', e.target.files[0] || null); e.target.value = '' }} />
            <div className="prd-file-row">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => videoRef.current?.click()}>
                <Film size={13} /> {form.video || existingVideo ? 'Replace video' : 'Upload video'}
              </button>
              {form.video && <span className="prd-file-name">{form.video.name}</span>}
              {!form.video && existingVideo && (
                <>
                  <a href={existingVideo.url} target="_blank" rel="noreferrer" className="prd-file-name">Current video</a>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setField('remove_video', true)}>Remove</button>
                </>
              )}
              {!form.video && active?.video && form.remove_video && (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setField('remove_video', false)}>Undo remove</button>
              )}
            </div>
            {fieldError('video')}
          </div>
          <div className="form-field">
            <label>Video Poster <small style={{ color: 'var(--text3)' }}>(optional)</small></label>
            <input ref={posterRef} type="file" accept="image/*" hidden
              onChange={(e) => { setField('video_poster', e.target.files[0] || null); e.target.value = '' }} />
            <div className="prd-file-row">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => posterRef.current?.click()}>
                <UploadCloud size={13} /> {form.video_poster ? 'Replace poster' : 'Upload poster'}
              </button>
              {form.video_poster && <span className="prd-file-name">{form.video_poster.name}</span>}
            </div>
            {fieldError('video_poster')}
          </div>

          {/* ---- Colors ---- */}
          <div className="form-field full">
            <label>Colors <small style={{ color: 'var(--text3)' }}>(swatches — text colour in live preview follows these)</small></label>
            <div className="prd-colors">
              {form.colors.map((c, i) => (
                <div key={i} className="prd-color-row">
                  <input type="color" value={c.hex} onChange={(e) => setColor(i, 'hex', e.target.value)} aria-label="Pick colour" />
                  <input className="input" placeholder="Color name e.g. Sand" value={c.name}
                    onChange={(e) => setColor(i, 'name', e.target.value)} />
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeColor(i)} aria-label="Remove colour">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-outline btn-sm" onClick={addColor}><Plus size={13} /> Add colour</button>
            </div>
          </div>

          {/* ---- Badges ---- */}
          <div className="form-field full">
            <label style={{ fontWeight: 600 }}>Show as</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <label><input type="checkbox" checked={form.is_new} onChange={(e) => setField('is_new', e.target.checked)} /> “New” badge &amp; New Arrivals slider</label>
              <label><input type="checkbox" checked={form.is_bestseller} onChange={(e) => setField('is_bestseller', e.target.checked)} /> Best seller</label>
              <label><input type="checkbox" checked={form.is_featured} onChange={(e) => setField('is_featured', e.target.checked)} /> Show on home page</label>
            </div>
          </div>

          {/* ---- Customization ---- */}
          <div className="form-field full">
            <label style={{ fontWeight: 600 }}>Customization</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <label><input type="checkbox" checked={form.is_customizable} onChange={(e) => setField('is_customizable', e.target.checked)} /> Customizable</label>
              <label><input type="checkbox" checked={form.allow_text_print} onChange={(e) => setField('allow_text_print', e.target.checked)} /> Add text</label>
              <label><input type="checkbox" checked={form.allow_logo_upload} onChange={(e) => setField('allow_logo_upload', e.target.checked)} /> Upload image / logo</label>
            </div>
          </div>
          <div className="form-field">
            <label>Max Text Length</label>
            <input type="number" min={1} className="input" value={form.text_max_length}
              onChange={(e) => setField('text_max_length', e.target.value)} />
            {fieldError('text_max_length')}
          </div>
          <div className="form-field">
            <label>Personalisation Price (₹)</label>
            <input type="number" min={0} className="input" value={form.customization_price}
              onChange={(e) => setField('customization_price', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Gift Wrap Price (₹)</label>
            <input type="number" min={0} className="input" value={form.gift_wrap_price}
              onChange={(e) => setField('gift_wrap_price', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Sort Order</label>
            <input type="number" min={0} className="input" value={form.sort_order}
              onChange={(e) => setField('sort_order', e.target.value)} />
          </div>

          {/* ---- Info accordions ---- */}
          <div className="form-field full">
            <label>Personalization Notes <small style={{ color: 'var(--text3)' }}>(one per line)</small></label>
            <textarea className="input" rows={3} value={form.personalization_notes}
              onChange={(e) => setField('personalization_notes', e.target.value)} />
          </div>
          <div className="form-field full">
            <label>Shipping &amp; Delivery</label>
            <textarea className="input" rows={3} value={form.shipping_info}
              onChange={(e) => setField('shipping_info', e.target.value)} />
          </div>
          <div className="form-field full">
            <label>Refund &amp; Return</label>
            <textarea className="input" rows={3} value={form.return_policy}
              onChange={(e) => setField('return_policy', e.target.value)} />
          </div>

          {/* ---- Filters (listing page) ---- */}
          <div className="form-field">
            <label>Likes</label>
            <input className="input" placeholder="Travel / Fashion & Accessories" value={form.likes}
              onChange={(e) => setField('likes', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Gift For</label>
            <input className="input" placeholder="Husband / Father / Friend…" value={form.gift_for}
              onChange={(e) => setField('gift_for', e.target.value)} />
          </div>
          <div className="form-field">
            <label>Occasion</label>
            <input className="input" placeholder="Birthday / Anniversary" value={form.occasion}
              onChange={(e) => setField('occasion', e.target.value)} />
          </div>

          {/* ---- Related ---- */}
          <div className="form-field full">
            <label>Related Products <small style={{ color: 'var(--text3)' }}>(Search and click)</small></label>
            <ProductPicker
              options={otherProducts}
              selectedIds={form.related_ids}
              onChange={(ids) => setField('related_ids', ids)}
            />
          </div>
          <div className="form-field full">
            <label>Frequently Bought Together <small style={{ color: 'var(--text3)' }}>(Search and click)</small></label>
            <ProductPicker
              options={otherProducts}
              selectedIds={form.bought_together_ids}
              onChange={(ids) => setField('bought_together_ids', ids)}
            />
          </div>

          {/* ---- SEO ---- */}
          <div className="form-field full">
            <label>Meta Title</label>
            <input className="input" value={form.meta_title} onChange={(e) => setField('meta_title', e.target.value)} />
          </div>
          <div className="form-field full">
            <label>Meta Description</label>
            <textarea className="input" rows={2} value={form.meta_description}
              onChange={(e) => setField('meta_description', e.target.value)} />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete product"
        message={`Delete ${active?.name || 'selected product'}? Its images, video and banners will also be removed.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        textarea.input { resize: vertical; min-height: 70px; }

        /* ---- page header (title left, action right) ---- */
        .pg-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-md); flex-wrap: wrap;
        }

        /* ---- toolbar search (stretches across the toolbar) ---- */
        .tb-search {
          flex: 1 1 260px; min-width: 0; margin: 0;
          display: flex; align-items: center; gap: 10px;
          height: 38px; padding: 0 12px; cursor: text;
          border: 1px solid var(--border); border-radius: 10px;
          background: #f6f8ff; color: var(--text3);
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tb-search:focus-within {
          border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16);
        }
        .tb-search svg { flex-shrink: 0; }
        .tb-search input {
          flex: 1; min-width: 0; height: 100%; padding: 0; margin: 0;
          border: 0; outline: 0; box-shadow: none; background: transparent;
          font: inherit; font-size: var(--fs-sm); color: var(--text1);
        }
        .tb-search input::placeholder { color: var(--text3); }

        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem;
          padding: 0.6rem 0.9rem; border-radius: 10px;
        }
        .sel-trigger.cat-filter-select { width: auto; min-width: 180px; flex-shrink: 0; }
        @media (max-width: 768px) {
          .sel-trigger.cat-filter-select { min-width: 140px; flex: 1; }
        }

        /* ── gallery ── */
        .prd-gallery { display: flex; flex-wrap: wrap; gap: 10px; }
        .prd-thumb, .prd-add {
          position: relative; width: 92px; height: 92px;
          border-radius: var(--radius-md); overflow: hidden;
          border: 1px solid var(--border); background: var(--bg-raised);
        }
        .prd-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .prd-thumb.removed img { opacity: .25; filter: grayscale(1); }
        .prd-thumb-btn {
          position: absolute; top: 4px; right: 4px;
          width: 22px; height: 22px; border-radius: 50%;
          display: grid; place-items: center; cursor: pointer;
          background: rgba(2, 6, 23, .7); color: #fff; border: 0;
        }
        .prd-thumb-btn:hover { background: rgba(239, 68, 68, .85); }
        .prd-main-tag, .prd-new-tag {
          position: absolute; left: 4px; bottom: 4px;
          font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 10px;
          background: rgba(2, 6, 23, .75); color: #fff;
        }
        .prd-new-tag { background: rgba(16, 185, 129, .85); }
        .prd-add {
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
          border: 1.5px dashed var(--border-active); color: var(--text2);
          font-size: var(--fs-xs); cursor: pointer;
          transition: border-color var(--ease-base), background var(--ease-base);
        }
        .prd-add:hover { border-color: var(--accent); background: var(--bg-hover); }

        .prd-file-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; min-height: 36px; }
        .prd-file-name { font-size: var(--fs-xs); color: var(--text2); overflow: hidden; text-overflow: ellipsis; max-width: 180px; white-space: nowrap; }

        /* ── hero banner ── */
        .prd-hero-block { padding: 12px; border: 1px dashed var(--border-active); border-radius: var(--radius-md); }
        .prd-hero-toggle { display: flex; align-items: center; gap: 8px; margin: 8px 0 12px; font-size: var(--fs-sm); cursor: pointer; }
        .prd-banner-box {
          position: relative; width: 100%; max-width: 640px;
          border: 1px solid var(--border); border-radius: var(--radius-md);
          background: var(--bg-raised); overflow: hidden;
        }
        .prd-banner-box img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .prd-banner-empty {
          position: absolute; inset: 0; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 6px;
          color: var(--text3); font-size: var(--fs-xs);
        }

        /* ── colors ── */
        .prd-colors { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
        .prd-color-row { display: flex; gap: 8px; align-items: center; width: 100%; max-width: 380px; }
        .prd-color-row input[type="color"] {
          width: 38px; height: 36px; padding: 2px; flex-shrink: 0; cursor: pointer;
          border: 1px solid var(--border); border-radius: var(--radius-md); background: transparent;
        }
        .prd-multi { min-height: 120px; }

        /* ── product picker ── */
        .pp-wrap { position: relative; display: flex; flex-direction: column; gap: 8px; }
        .pp-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .pp-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 3px 4px 3px 4px; max-width: 100%;
          border: 1px solid var(--border); border-radius: 999px;
          background: var(--bg-raised); color: var(--text1); font-size: var(--fs-xs);
        }
        .pp-chip img { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
        .pp-chip-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 180px; }
        .pp-chip button {
          width: 18px; height: 18px; border-radius: 50%; border: 0; cursor: pointer;
          display: grid; place-items: center; background: transparent; color: var(--text2);
        }
        .pp-chip button:hover { background: rgba(239, 68, 68, .85); color: #fff; }

        .pp-menu {
          position: absolute; top: 100%; left: 0; right: 0; z-index: 30; margin-top: 4px;
          max-height: 240px; overflow-y: auto;
          background: var(--bg-raised, #fff); border: 1px solid var(--border);
          border-radius: var(--radius-md); box-shadow: 0 12px 28px rgba(0, 0, 0, .18);
        }
        .pp-item {
          width: 100%; display: flex; align-items: center; gap: 10px;
          padding: 8px 10px; border: 0; background: transparent; cursor: pointer;
          text-align: left; color: var(--text1);
        }
        .pp-item:hover { background: var(--bg-hover); }
        .pp-item img, .pp-noimg {
          width: 30px; height: 30px; border-radius: var(--radius-md); object-fit: cover; flex-shrink: 0;
        }
        .pp-noimg { display: grid; place-items: center; background: var(--bg-hover); color: var(--text3); }
        .pp-item-name { flex: 1; font-size: var(--fs-sm, 13px); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .pp-item-price { font-size: var(--fs-xs); color: var(--text3); }
        .pp-empty { padding: 12px; font-size: var(--fs-xs); color: var(--text3); text-align: center; }

        /* ── GST / price breakdown ── */
        .prd-taxbox { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 8px 16px;
          padding: 12px 14px; border: 1px dashed var(--border-active); border-radius: var(--radius-md); background: var(--bg-raised); }
        .prd-taxbox > div { display: flex; justify-content: space-between; gap: 10px; font-size: var(--fs-xs); color: var(--text2); }
        .prd-taxbox b { color: var(--text1); }
        .prd-taxbox-total { grid-column: 1 / -1; padding-top: 8px; border-top: 1px solid var(--border); font-weight: 700; }
      `}</style>
    </div>
  )
}